import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Stack } from '@mui/material';
import { Typography, colors, Switch, Button, ExpandableTypography } from '@sweep-io/sweep-design';
import { Delete, NotePencil } from '@sweep-io/sweep-design/dist/icons';
import { BestPractice } from './types';
import { SalesforceConfigurationType } from '@server/ai';
import { AiAgent } from '../aiAgentCenter/aiAgentsConsts';
import { Commands } from '../../types/enums/Common';
import { ConfigurationToName } from '../documentation/dependencies/types';
import { ActionsMenu, ActionsMenuAction } from '../common/actions-menu/ActionsMenu';
import Severity from './Severity';
import ConfirmDestructiveDialog from '../common/dialogs/ConfirmDestructiveDialog';
import useConfirm from '../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { useBestPractices } from './useBestPractices';
import isEqual from 'lodash/isEqual';
import BestPracticeEditSeverity from './BestPracticeEditSeverity';
import BestPracticeEditResource from './BestPracticeEditResource';
import { TruncatedTextTooltip } from '../common/TruncatedTextTooltip';
import { usePermissions } from '../../permissions/usePermissions';
import EditName from '../common/editable-card/EditName';
import EditTextarea from '../common/editable-card/EditTextarea';
import { getMetadataLabel } from '../documentation/tabs/getMetadataLabel';
import EditableCardLayout from '../common/editable-card/EditableCardLayout';
import { BestPracticeSelectorsWrapper } from './BestPracticeSelectorsWrapper';

interface BestPracticeCardBaseProps {
  bestPractice: BestPractice;
  isCustom: boolean;
  index: number;
  onIsPristineChange?: (isPristine: boolean) => void;
  agent?: AiAgent | null;
  onToggleActive: (isActive: boolean) => void | Promise<void>;
  isToggleChecked: boolean;
  showToggle: boolean;
  crmOrgId: string;
}

const hasOnlySeverityChanged = (prev: BestPractice, next: BestPractice) => {
  if (prev.severity === next.severity) return false;

  return (
    prev.text === next.text &&
    prev.name === next.name &&
    prev.applicableResourceType === next.applicableResourceType
  );
};

const getActionsMenu = ({
  isCustom,
  isAllowedDelete,
}: {
  isCustom: boolean;
  isAllowedDelete: boolean;
}) => {
  const actionsMenu: ActionsMenuAction<Commands>[] = [
    {
      value: Commands.Edit,
      label: isCustom ? 'Edit' : 'Edit Impact',
      actionIcon: <NotePencil size="medium" />,
    },
  ];
  if (isCustom) {
    actionsMenu.push({
      value: Commands.Delete,
      label: 'Delete',
      actionIcon: <Delete color={colors.blush[600]} />,
      actionSx: { color: colors.blush[600] },
      disabled: !isAllowedDelete,
      disabledTooltip: isAllowedDelete ? '' : 'To delete best practice, please contact your admin.',
    });
  }
  return actionsMenu;
};

const BestPracticeCardBase: React.FC<BestPracticeCardBaseProps> = ({
  bestPractice,
  isCustom,
  index,
  onIsPristineChange,
  agent,
  onToggleActive,
  isToggleChecked,
  showToggle,
  crmOrgId,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [name, setName] = useState(bestPractice.name);
  const [text, setText] = useState(bestPractice.text);
  const [severity, setSeverity] = useState(bestPractice.severity);
  const [resourceType, setResourceType] = useState<SalesforceConfigurationType>(
    bestPractice.applicableResourceType,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { openConfirm, onCancel, onConfirm } = useConfirm();
  const { updateBestPracticeDetails, removeBestPractice, removeAndCreateBestPractice } =
    useBestPractices(bestPractice.applicableDomain);
  const { evaluatePermissions } = usePermissions();
  const [isAllowedEdit, isAllowedDelete] = evaluatePermissions([
    { permission: ['edit:best-practices', 'delete:best-practices'], crmOrgId },
  ]);
  const actionsMenu = getActionsMenu({ isCustom, isAllowedDelete });
  const isValid = !!name && !!text && !!severity && !!resourceType;

  const updatedBestPractice = useMemo(
    () => ({
      ...bestPractice,
      name,
      text,
      severity,
      applicableResourceType: resourceType,
    }),
    [bestPractice, name, resourceType, severity, text],
  );

  const isPristine = isEqual(bestPractice, updatedBestPractice);

  useEffect(() => {
    if (!isEditMode) return;
    onIsPristineChange?.(isPristine);

    // When edit mode is exited, report form as pristine
    return () => {
      onIsPristineChange?.(true);
    };
  }, [isEditMode, isPristine, onIsPristineChange]);

  const handleSave = useCallback(async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    if (!isPristine) {
      if (hasOnlySeverityChanged(bestPractice, updatedBestPractice)) {
        await updateBestPracticeDetails(bestPractice.id, {
          severity,
        });
      } else {
        await removeAndCreateBestPractice(updatedBestPractice, index);
      }
    }

    setIsSubmitting(false);
    closeEditMode();
  }, [
    bestPractice,
    index,
    isPristine,
    isValid,
    removeAndCreateBestPractice,
    severity,
    updateBestPracticeDetails,
    updatedBestPractice,
  ]);

  const handleDeleteBestPractice = useCallback(
    async (id: string) => {
      const isConfirmed = await openConfirm(
        <ConfirmDestructiveDialog
          open
          onConfirm={onConfirm}
          onClose={onCancel}
          title="Delete best practice?"
          confirmText="Delete"
          cancelText="Cancel"
        >
          This best practice will be permanently removed.
        </ConfirmDestructiveDialog>,
      );

      if (isConfirmed) {
        await removeBestPractice(id);
      }
    },
    [removeBestPractice, openConfirm, onCancel, onConfirm],
  );

  const handleActionClick = (action: any) => {
    if (action.value === Commands.Delete && bestPractice.id) {
      handleDeleteBestPractice(bestPractice.id);
    }
    if (action.value === Commands.Edit) {
      setIsEditMode(true);
    }
  };

  const closeEditMode = () => {
    setIsEditMode(false);
  };

  // Reset is needed for the next time entering the edit mode
  const resetAndClose = () => {
    setName(bestPractice.name);
    setText(bestPractice.text);
    setSeverity(bestPractice.severity);
    setResourceType(bestPractice.applicableResourceType);
    closeEditMode();
  };

  const nameJsx =
    isEditMode && isCustom && !isSubmitting ? (
      <EditName setName={setName} name={name} placeholder="Enter a name for this best practice" />
    ) : (
      <TruncatedTextTooltip variant="h5">{bestPractice.name}</TruncatedTextTooltip>
    );

  const textJsx =
    isEditMode && isCustom && !isSubmitting ? (
      <EditTextarea setText={setText} text={text} placeholder="Define a standard to follow..." />
    ) : (
      <ExpandableTypography variant="body" color={colors.grey[700]} rows={2}>
        {bestPractice.text}
      </ExpandableTypography>
    );

  const severityJsx =
    isEditMode && !isSubmitting ? (
      <BestPracticeEditSeverity
        setSeverity={setSeverity}
        severity={severity}
        autoOpen={!isCustom}
      />
    ) : (
      <Severity severity={bestPractice.severity} />
    );

  const resourceJsx =
    isEditMode && isCustom && !isSubmitting ? (
      <BestPracticeEditResource
        setResourceType={setResourceType}
        resourceType={resourceType}
        agent={agent}
      />
    ) : (
      <Typography variant="caption-medium" color={colors.grey[500]}>
        {ConfigurationToName[bestPractice.applicableResourceType as string] ||
          getMetadataLabel(bestPractice.applicableResourceType)}
      </Typography>
    );

  const buttonsJsx = isEditMode ? (
    <Stack direction="row" gap={1}>
      <Button variant="system" size="tiny" onClick={resetAndClose} disabled={isSubmitting}>
        Cancel
      </Button>
      <Button
        variant="primary"
        size="tiny"
        onClick={handleSave}
        loading={isSubmitting}
        disabled={!isValid}
      >
        Save
      </Button>
    </Stack>
  ) : undefined;

  const actionsJsx = isEditMode ? undefined : (
    <Stack direction="row" gap={1} alignItems="center">
      {showToggle && (
        <Switch
          checked={isToggleChecked}
          onChange={(e) => onToggleActive(e.target.checked)}
          size="small"
        />
      )}
      <ActionsMenu
        actions={actionsMenu}
        onClick={handleActionClick}
        iconSize="tiny"
        menuPlacement="left"
        disabled={!isAllowedEdit}
        iconTooltip={isAllowedEdit ? '' : 'To edit best practice, please contact your admin.'}
      />
    </Stack>
  );

  return (
    <EditableCardLayout
      borderColor={isEditMode ? colors.blue[600] : undefined}
      nameJsx={nameJsx}
      textJsx={textJsx}
      bottomLeftJsx={
        <BestPracticeSelectorsWrapper
          isEditMode={isEditMode}
          severityJsx={severityJsx}
          resourceJsx={resourceJsx}
        />
      }
      updateCardActionsJsx={buttonsJsx}
      readCardActionsJsx={actionsJsx}
    />
  );
};

export default BestPracticeCardBase;
